import { ApolloClient, useMutation, MutationUpdaterFn } from '@apollo/client'
import React from 'react'

import { flattenErrors, FormErrors } from './useForm'

interface ErrorSavingState {
  errors: FormErrors | null
  isSaving: boolean
}

export const useMutationResult = <TData, TVariables, TResult>({
  mutation,
  onCompleted,
  onError,
  successField,
}: {
  mutation: any
  onCompleted?: (data?: TResult, client?: ApolloClient<object>) => void
  onError?: (errors: any, client?: ApolloClient<object>) => void
  successField: string
}) => {
  const [state, setState] = React.useState<ErrorSavingState>({
    errors: null,
    isSaving: false,
  })
  const [mutateFn, { client, loading }] = useMutation<TData, TVariables>(
    mutation,
  )

  const clearErrors = React.useCallback(() => {
    setState({ errors: null, isSaving: state.isSaving })
  }, [state, setState])

  const mutate = React.useCallback(
    async (variables: TVariables, updateFn?: MutationUpdaterFn<TData>) => {
      setState({ ...state, isSaving: true })
      return mutateFn({ variables, update: updateFn })
        .then(({ data, errors }) => {
          if (errors) {
            setState({ errors: flattenErrors(errors), isSaving: false })
            onError?.(errors, client)
          } else if (data) {
            setState({ errors: null, isSaving: false })
            onCompleted?.(data[successField] as TResult, client)
          }
          return { data, errors }
        })
        .catch((e) => {
          onError?.([{ message: 'Something went wrong' }], client)
          throw e
        })
    },
    [client, mutateFn, onCompleted, onError, state, successField],
  )

  return React.useMemo(
    () => ({
      ...state,
      clearErrors,
      isLoading: loading,
      mutate,
    }),
    [clearErrors, loading, mutate, state],
  )
}
